import React, { useState } from "react";
import "./Cart.css";
import SuccessButton from "../shared/SuccessButton";
import OrderForm from "./OrderForm";
import settings from "../../assets/admin/icons/settings.svg";
import placeholderImage from "../../assets/admin/images/placeholder.svg";
import PrimaryButtonDark from "../shared/PrimaryButtonDark";
import CartItemOptionsModal from "./CartItemOptionsModal";

const S3_BUCKET_NAME = "resto-online";
const S3_BUCKET_REGION = "eu-north-1";
const S3_BASE_URL = `https://${S3_BUCKET_NAME}.s3.${S3_BUCKET_REGION}.amazonaws.com/`;
const VAT_RATE = 0.06;

const CartWithOrderForm = ({ 
  cartItems, 
  onIncreaseQuantity, 
  onDecreaseQuantity,
  onUpdateCartItems,
  className,
  onClose,
  id
}) => {
  const [view, setView] = useState("cart"); // "cart" or "orderForm"
  const [editingItem, setEditingItem] = useState(null);
  const isMobileCart = id === "mobile-cart";

  const handleEditItem = (item) => {
    setEditingItem(item);
  };

  const handleUpdateItem = (updatedItem) => {
    // Find the item in the cart and update its options
    const updatedCartItems = cartItems.map(item => {
      if (item._id === updatedItem._id && 
          JSON.stringify(item.selectedOptions) === JSON.stringify(editingItem.selectedOptions)) {
        return {
          ...item,
          selectedOptions: updatedItem.selectedOptions
        };
      }
      return item;
    });

    // Update the cart items in the parent component
    onUpdateCartItems(updatedCartItems);
    setEditingItem(null);
  };

  // Function to calculate option prices
  const calculateOptionPrices = (selectedOptions, productOptions) => {
    let optionPrices = 0;

    if (selectedOptions && productOptions) {
      Object.entries(selectedOptions).forEach(([index, value]) => {
        const option = productOptions[index];
        if (!option) return;

        if (option.type === "select") {
          const selectedChoice = option.options[value];
          const price = selectedChoice?.price?.$numberDecimal 
            ? parseFloat(selectedChoice.price.$numberDecimal)
            : parseFloat(selectedChoice?.price || 0);
          optionPrices += price;
        } else if (option.type === "multiselect") {
          value.forEach((choiceIndex) => {
            const choice = option.options[choiceIndex];
            if (choice) {
              const price = choice?.price?.$numberDecimal 
                ? parseFloat(choice.price.$numberDecimal)
                : parseFloat(choice?.price || 0);
              optionPrices += price;
            }
          });
        }
      });
    }
    return optionPrices;
  };

  // Function to calculate total prices
  const calculatePrices = (cartItems) => {
    let totalDiscount = 0;
    let totalInclVat = 0;

    cartItems.forEach((item) => {
      // Calculate the base price of the product
      const basePrice = parseFloat(item.price?.$numberDecimal || item.price) || 0;

      // Use the separate function to calculate option prices
      const optionPrices = calculateOptionPrices(item.selectedOptions, item.productOptions);

      // Calculate the total price including options
      const totalItemPrice = basePrice + optionPrices;

      if (item.discount) {
        const discountAmount = totalItemPrice * (item.discount / 100);
        totalDiscount += discountAmount * item.quantity;
        totalInclVat += (totalItemPrice - discountAmount) * item.quantity;
      } else {
        totalInclVat += totalItemPrice * item.quantity;
      }
    });

    // Calculate subtotal, VAT, and total
    const subtotal = totalInclVat / (1 + VAT_RATE);
    const vat = totalInclVat - subtotal;

    return {
      subtotal: subtotal.toFixed(2),
      vat: vat.toFixed(2),
      total: totalInclVat.toFixed(2),
      totalDiscount: totalDiscount.toFixed(2),
    };
  };

  const renderInclusiveProducts = (item) => {
    if (item.type === "menu" && item.products?.length > 0) {
      return <p className="f-s mt-5">Inclusief: {item.products.map((p) => p.name).join(", ")}</p>;
    }
    return null;
  };

  const prices = calculatePrices(cartItems);

  return (
    <div className={`shop-cart g-30 bg-w br-15 border-light py-10 px-10 g-20 ${className || ''} ${isMobileCart ? 'mobile-cart' : ''}`} id={id}>
      {/* Navigation Bar */}
      <div className="navigation-bar">
        {view === "orderForm" ? (
          <button className="invisible-btn f-r" onClick={() => setView("cart")}>
            ← Terug naar winkelmandje
          </button>
        ) : (
          // Only show the close button in mobile view
          isMobileCart && (
            <button className="invisible-btn f-r close-cart-button" onClick={onClose}>
              × Sluiten
            </button>
          )
        )}
      </div>

      {/* View Content */}
      {view === "cart" ? (
        <>
          <h2 className="d-f-c">Winkelmandje</h2>
          {cartItems.length > 0 ? (
            <>
              <ul className="g-10">
                {cartItems.map((item) => {
                  const basePrice = parseFloat(item.price.$numberDecimal || item.price) || 0;
                  const optionPrices = calculateOptionPrices(item.selectedOptions, item.productOptions);
                  const totalItemPrice = parseFloat(basePrice || 0) + parseFloat(optionPrices || 0);
                  const discountedPrice = item.discount
                    ? parseFloat((totalItemPrice * (1 - item.discount / 100)).toFixed(2))
                    : parseFloat(totalItemPrice.toFixed(2));
                  const imageUrl = item.hasImage 
                    ? `${S3_BASE_URL}${item._id}?t=${Date.now()}` 
                    : placeholderImage;

                  return (
                    <li key={`${item._id}-${JSON.stringify(item.selectedOptions)}`} className="cart-item g-5">
                      <div className="cart-item-content">
                        <div className="cart-item-left">
                          <img src={imageUrl} alt={item.name} className="cart-item-image" />
                        </div>
                        <div className="cart-item-right">
                          <div className="cart-item-header">
                            <h4>{item.name}</h4>
                          </div>
                          <div className="cart-item-details">
                            {item.selectedOptions && Object.entries(item.selectedOptions).map(([optionIndex, value]) => {
                              const option = item.productOptions[parseInt(optionIndex)];
                              if (!option) return null;

                              let displayValue = "";
                              if (option.type === "select") {
                                const selectedChoice = option.options[value];
                                const price = selectedChoice?.price?.$numberDecimal 
                                  ? parseFloat(selectedChoice.price.$numberDecimal)
                                  : parseFloat(selectedChoice?.price || 0);
                                displayValue = selectedChoice ? `${selectedChoice.name}${price > 0 ? ` (+€${price.toFixed(2)})` : ''}` : "";
                              } else if (option.type === "multiselect") {
                                const selectedChoices = value.map(index => {
                                  const choice = option.options[index];
                                  const price = choice?.price?.$numberDecimal 
                                    ? parseFloat(choice.price.$numberDecimal)
                                    : parseFloat(choice?.price || 0);
                                  return choice ? `${choice.name}${price > 0 ? ` (+€${price.toFixed(2)})` : ''}` : "";
                                }).filter(Boolean);
                                displayValue = selectedChoices.join(", ");
                              } else if (option.type === "text") {
                                displayValue = value;
                              }

                              return displayValue ? (
                                <p key={optionIndex} className="c-g">
                                  {option.name}: {displayValue}
                                </p>
                              ) : null;
                            })}
                          </div>
                          <div className="cart-item-footer">
                            <div>
                              {item.discount ? (
                                <>
                                  <span className="original-price">€{totalItemPrice.toFixed(2)}</span>{" "}
                                  <span className="discounted-price">€{discountedPrice.toFixed(2)}</span>
                                </>
                              ) : (
                                <span>€{totalItemPrice.toFixed(2)}</span>
                              )}
                            </div>
                            <div className="cart-item-quantity">
                              <button
                                className="quantity-button"
                                onClick={() => onDecreaseQuantity(item._id, item.selectedOptions)}
                              >
                                -
                              </button>
                              <span>{item.quantity}</span>
                              <button
                                className="quantity-button"
                                onClick={() => onIncreaseQuantity(item._id, item.selectedOptions)}
                              >
                                +
                              </button>
                              {item.productOptions && item.productOptions.length > 0 && (
                                <img 
                                  src={settings} 
                                  alt="settings" 
                                  className="settings-icon"
                                  onClick={() => handleEditItem(item)}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      {renderInclusiveProducts(item)}
                    </li>
                  );
                })}
              </ul>
              <div className="price-summary">
                <div className="price-row f-m f-w-m">
                  <span>Subtotaal</span>
                  <span>€{prices.subtotal}</span>
                </div>
                <div className="price-row f-s">
                  <span>BTW (6%)</span>
                  <span>€{prices.vat}</span>
                </div>
                <div className="price-row f-s">
                  <span>Korting</span>
                  <span className="c-succes">-€{prices.totalDiscount}</span>
                </div>
                <div className="price-row total">
                  <span>Totaal</span>
                  <span>€{prices.total}</span>
                </div>
              </div>
              <div className="cart-actions">
                <PrimaryButtonDark
                  className="w-100p"
                  onClick={() => setView("orderForm")}
                  disabled={cartItems.length === 0}
                >
                  Bestelling plaatsen
                </PrimaryButtonDark>
                
                {/* Add a close button at the bottom for mobile */}
                {isMobileCart && (
                  <button 
                    className="invisible-btn mt-10 w-100p t-c f-m" 
                    onClick={onClose}
                  >
                    Verder winkelen
                  </button>
                )}
              </div>
            </>
          ) : (
            <>
              <p>Je winkelmandje is leeg.</p>
              {isMobileCart && (
                <button 
                  className="invisible-btn mt-10 w-100p t-c f-m" 
                  onClick={onClose}
                >
                  Verder winkelen
                </button>
              )}
            </>
          )}
        </>
      ) : (
        <OrderForm
          cartItems={cartItems}
          prices={prices}
          onBack={() => setView("cart")}
        />
      )}
      
      {editingItem && (
        <CartItemOptionsModal
          isOpen={!!editingItem}
          onClose={() => setEditingItem(null)}
          product={editingItem}
          onUpdateItem={handleUpdateItem}
        />
      )}
    </div>
  );
};

export default CartWithOrderForm;
